import { validateReturnTo } from '@/components/Prescriptions/criteriaNavigation'
import { parseMedicationCheckInProgressLabOrderId } from '@/lib/medicationCheckInLabFlow'

const PRESCRIPTION_CHECK_IN_PATH_PATTERN = /^\/prescriptions\/[A-Za-z0-9_-]+\/check-in(\/|$)/

export function buildPrescriptionCheckInPath(prescriptionId: string): string {
    return `/prescriptions/${encodeURIComponent(prescriptionId)}/check-in`
}

export function buildPrescriptionDetailPath(prescriptionId: string): string {
    return `/prescriptions/${encodeURIComponent(prescriptionId)}`
}

/** `returnTo` is where the labs landing's back/skip goes: the check-in hub (default) or the prescription detail page. */
export function buildPrescriptionCheckInLabsPath(prescriptionId: string, returnTo?: string): string {
    const basePath = `${buildPrescriptionCheckInPath(prescriptionId)}/labs`
    if (!returnTo) return basePath
    return `${basePath}?return_to=${encodeURIComponent(returnTo)}`
}

/** Back/skip target for a check-in step landing: the prescription detail page only when explicitly passed, else the check-in hub. */
function resolvePrescriptionCheckInStepBackPath(
    prescriptionId: string,
    returnTo: string | null | undefined,
): string {
    const validated = validateReturnTo(returnTo ?? null)
    const detailPath = buildPrescriptionDetailPath(prescriptionId)
    return validated === detailPath ? detailPath : buildPrescriptionCheckInPath(prescriptionId)
}

/** Resolves the labs landing page's back/skip target: the prescription detail page only when explicitly passed, else the check-in hub. */
export function resolvePrescriptionCheckInLabsBackPath(
    prescriptionId: string,
    returnTo: string | null | undefined,
): string {
    return resolvePrescriptionCheckInStepBackPath(prescriptionId, returnTo)
}

/**
 * Grouped questionnaire landing for a single prescription's check-in. Mirrors the labs
 * landing: `returnTo` is where the landing's back/skip goes (the check-in hub by default, or
 * the prescription detail page when the flow was entered straight from it).
 */
export function buildPrescriptionCheckInQuestionnaireLandingPath(
    prescriptionId: string,
    returnTo?: string,
): string {
    const basePath = `${buildPrescriptionCheckInPath(prescriptionId)}/questionnaire`
    if (!returnTo) return basePath
    return `${basePath}?return_to=${encodeURIComponent(returnTo)}`
}

/** Resolves the grouped questionnaire landing's back/skip target: the prescription detail page only when explicitly passed, else the check-in hub. */
export function resolvePrescriptionCheckInQuestionnaireBackPath(
    prescriptionId: string,
    returnTo: string | null | undefined,
): string {
    return resolvePrescriptionCheckInStepBackPath(prescriptionId, returnTo)
}

export function isPrescriptionCheckInReturnTo(path: string): boolean {
    return PRESCRIPTION_CHECK_IN_PATH_PATTERN.test(path)
}

export function buildPrescriptionCheckInOrdersLabsUrl(prescriptionId: string, labOrderId: string): string {
    const returnTo = buildPrescriptionCheckInPath(prescriptionId)
    return `/orders/${encodeURIComponent(labOrderId)}/labs?return_to=${encodeURIComponent(returnTo)}`
}

export function parsePrescriptionCheckInIdFromPath(path: string): string | null {
    const match = path.match(/^\/prescriptions\/([A-Za-z0-9_-]+)\/check-in/)
    return match?.[1] ?? null
}

export function validatePrescriptionCheckInReturnTo(value: string | null | undefined): string | null {
    const validated = validateReturnTo(value ?? null)
    if (!validated || !isPrescriptionCheckInReturnTo(validated)) return null
    return validated
}

export type PrescriptionCheckInProgressFrom = 'lab' | 'questionnaire'

export function buildPrescriptionCheckInCompletePath(prescriptionId: string): string {
    return `${buildPrescriptionCheckInPath(prescriptionId)}/complete`
}

export function buildPrescriptionCheckInProgressPath(
    prescriptionId: string,
    from: PrescriptionCheckInProgressFrom,
    options?: { labOrderId?: string | null },
): string {
    const params = new URLSearchParams({ from })
    const labOrderId = parseMedicationCheckInProgressLabOrderId(options?.labOrderId)
    if (labOrderId) {
        params.set('lab_order_id', labOrderId)
    }
    return `${buildPrescriptionCheckInPath(prescriptionId)}/progress?${params.toString()}`
}

/** Matches account check-in: exit routing applies when return_to is the check-in hub, not nested step URLs. */
export function isPrescriptionCheckInReturnPath(returnToPath: string | null | undefined): boolean {
    const validated = validatePrescriptionCheckInReturnTo(returnToPath)
    if (!validated) return false
    const prescriptionId = parsePrescriptionCheckInIdFromPath(validated)
    if (!prescriptionId) return false
    return validated === buildPrescriptionCheckInPath(prescriptionId)
}
